"use client";

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBasketShopping,
  faShoppingBasket,
} from "@fortawesome/free-solid-svg-icons";

// Import Swiper styles
import "swiper/css";
import "swiper/css/navigation";

// Custom SVG Icons as components
const PencilIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" />
  </svg>
);

const UploadIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
    <path d="M12,11L16,15H13V19H11V15H8L12,11Z" />
  </svg>
);

const DownloadIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M5,20H19V18H5M19,9H15V3H9V9H5L12,16L19,9Z" />
  </svg>
);

const PaymentIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M20,4H4C2.89,4 2,4.89 2,6V18A2,2 0 0,0 4,20H20A2,2 0 0,0 22,18V6C22,4.89 21.11,4 20,4M20,18H4V12H20V18M20,8H4V6H20V8Z" />
  </svg>
);

const HomeIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M10,20V14H14V20H19V12H22L12,3L2,12H5V20H10Z" />
  </svg>
);

const MenuIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
    <path d="M3,6H21V8H3V6M3,11H21V13H3V11M3,16H21V18H3V16Z" />
  </svg>
);

const CloseIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
    <path d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z" />
  </svg>
);

const ArrowLeftIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M20,11V13H8L13.5,18.5L12.08,19.92L4.16,12L12.08,4.08L13.5,5.5L8,11H20Z" />
  </svg>
);

const ShoppingCartIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
    <path d="M17,18C15.89,18 15,18.89 15,20A2,2 0 0,0 17,22A2,2 0 0,0 19,20C19,18.89 18.1,18 17,18M1,2V4H3L6.6,11.59L5.24,14.04C5.09,14.32 5,14.65 5,15A2,2 0 0,0 7,17H19V15H7.42A0.25,0.25 0 0,1 7.17,14.75C7.17,14.7 7.18,14.66 7.2,14.63L8.1,13H15.55C16.3,13 16.96,12.58 17.3,11.97L20.88,5H5.21L4.27,3H1M7,18C5.89,18 5,18.89 5,20A2,2 0 0,0 7,22A2,2 0 0,0 9,20C9,18.89 8.1,18 7,18Z" />
  </svg>
);

const LogOutIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M16,17V14H9V10H16V7L21,12L16,17M14,2A2,2 0 0,1 16,4V6H14V4H5V20H14V18H16V20A2,2 0 0,1 14,22H5A2,2 0 0,1 3,20V4A2,2 0 0,1 5,2H14Z" />
  </svg>
);

const ChevronRightIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M8.59,16.58L13.17,12L8.59,7.41L10,6L16,12L10,18L8.59,16.58Z" />
  </svg>
);

const Homarequests = () => {
  const [activeMenuItem, setActiveMenuItem] = useState("Online Prayer Requests");
  const [isMobile, setIsMobile] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [cartCount, setCartCount] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (!mobile) {
        setSidebarCollapsed(false);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Fetch cart count
  const fetchCartCount = async () => {
    try {
      const token = localStorage.getItem("jwtToken");
      if (!token) return;

      const response = await fetch("/api/dashboard", {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.user && typeof data.user.cart_count === "number") {
          setCartCount(data.user.cart_count);
        }
      }
    } catch (error) {
      console.error("Error fetching cart count:", error);
    }
  };

  useEffect(() => {
    fetchCartCount();
    const handleCartUpdate = () => fetchCartCount();
    window.addEventListener("cartUpdated", handleCartUpdate);
    return () => window.removeEventListener("cartUpdated", handleCartUpdate);
  }, []);

  const menuItems = [
    { name: "Home", icon: HomeIcon, hasSubmenu: true },
    { name: "Online Prayer Requests", icon: PencilIcon, hasSubmenu: true },
    { name: "Upload Prayer Forms", icon: UploadIcon, hasSubmenu: false },
    { name: "Download Prayer Forms", icon: DownloadIcon, hasSubmenu: true },
    { name: "Payments & Offerings", icon: PaymentIcon, hasSubmenu: false },
    { name: "My Basket", icon: ShoppingCartIcon, hasSubmenu: false },
    { name: "Sign Out", icon: LogOutIcon, hasSubmenu: false },
  ];

  const styles = {
    container: {
      display: "flex",
      minHeight: "100vh",
      backgroundColor: "#f5f5f5",
      fontFamily: "Arial, sans-serif",
      position: "relative",
    },
    header: {
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      height: "80px",
      backgroundColor: "#555b6e",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "0 20px",
      zIndex: 1000,
      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
    },
    headerLeft: {
      display: "flex",
      alignItems: "center",
      gap: "20px",
    },
    headerRight: {
      display: "flex",
      alignItems: "center",
      gap: "15px",
    },
    menuButton: {
      background: "none",
      border: "none",
      color: "white",
      fontSize: "1.5rem",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      padding: "8px",
    },
    goBackButton: {
      background: "none",
      border: "none",
      color: "white",
      display: "flex",
      alignItems: "center",
      gap: "8px",
      cursor: "pointer",
      fontSize: "1rem",
      padding: "8px 12px",
    },
    cartIcon: {
      position: "relative",
      color: "white",
      fontSize: "1.5rem",
      cursor: "pointer",
      padding: "8px",
    },
    cartBadge: {
      position: "absolute",
      top: "0px",
      right: "0px",
      backgroundColor: "#ef4444",
      color: "white",
      borderRadius: "50%",
      width: "20px",
      height: "20px",
      fontSize: "0.75rem",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    signOutButton: {
     backgroundColor: "#89b0ae",
      color: "white",
      border: "none",
      padding: "8px 16px",
      borderRadius: "4px",
      cursor: "pointer",
      fontSize: "0.9rem",
    },
    sidebar: {
       backgroundColor: "#dceeea",
      paddingTop: "80px",
      minHeight: "100vh",
      position: "fixed",
      width: isMobile ? (sidebarCollapsed ? "60px" : "80%") : (sidebarCollapsed ? "60px" : "250px"),
      left: 0,
      transition: "width 0.3s ease",
      zIndex: 999,
      overflowY: "auto",
    },
    overlay: {
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "rgba(0,0,0,0.5)",
      zIndex: 998,
      display: isMobile && !sidebarCollapsed ? "block" : "none",
    },
    menuItem: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "14px 20px",
      color: "#ffffff",
      cursor: "pointer",
      fontSize: "0.95rem",
      transition: "background-color 0.2s, transform 0.1s ease-in-out",
      borderTopLeftRadius: "30px",
      borderBottomLeftRadius: "30px",
      marginBottom: "12px",
      gap: "12px",
      backgroundColor: "#89b0ae",
      fontWeight: "500",
      borderBottom: "none",
      width: "100%",
    },
    menuItemCollapsed: {
      justifyContent: "center",
      padding: "14px 0",
    },
    menuItemActive: {
      backgroundColor: "#4b5563",
    },
    menuItemLeft: {
      display: "flex",
      alignItems: "center",
      gap: "12px",
    },
    menuItemText: {
      display: sidebarCollapsed ? "none" : "inline",
    },
    mainContent: {
      marginLeft: isMobile ? "0" : (sidebarCollapsed ? "60px" : "250px"),
      paddingTop: "80px",
      flex: 1,
      padding: isMobile ? "20px" : "30px",
      width: isMobile ? "100%" : `calc(100% - ${sidebarCollapsed ? "60px" : "250px"})`,
      transition: "margin-left 0.3s ease, width 0.3s ease",
    },
    titleSection: {
      textAlign: "center",
      marginBottom: "60px",
    },
    pageTitle: {
      fontSize: "28px",
      fontWeight: "400",
      color: "#6b7280",
      margin: "0",
    },
    swiperContainer: {
      position: "relative",
      maxWidth: "800px",
      margin: "0 auto",
      padding: "0 20px",
    },
    slideContainer: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "40px 20px",
      minHeight: "500px",
    },
    regularCard: {
      backgroundColor: "white",
      border: "3px solid #7fb3a3",
      borderRadius: "25px",
      padding: "30px",
      maxWidth: "700px",
      width: "100%",
      height: "400px",
      display: "flex",
      flexDirection: isMobile ? "column" : "row",
      alignItems: "center",
      gap: "30px",
      boxShadow: "0 8px 25px rgba(0,0,0,0.1)",
    },
    specialCard: {
      backgroundColor: "white",
      border: "3px solid #7fb3a3",
      borderRadius: "25px",
      padding: "30px",
      maxWidth: "700px",
      width: "100%",
      height: "400px",
      display: "flex",
      flexDirection: isMobile ? "column" : "row",
      alignItems: "center",
      gap: "30px",
      boxShadow: "0 8px 25px rgba(0,0,0,0.1)",
    },
    formPreviewSection: {
      flex: "1",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      maxWidth: isMobile ? "100%" : "300px",
      height: isMobile ? "200px" : "100%",
    },
    formPreviewImg: {
      width: "100%",
      height: "100%",
      objectFit: "cover",
      borderRadius: "15px",
      border: "2px solid #e5e7eb",
      maxWidth: "280px",
      maxHeight: "340px",
    },
    formPreviewImgSpecial: {
      width: "100%",
      height: "100%",
      objectFit: "cover",
      borderRadius: "15px",
      border: "2px solid #e5e7eb",
      maxWidth: "280px",
      maxHeight: "340px",
    },
    fallbackFormPreview: {
      backgroundColor: "#f9fafb",
      border: "2px solid #e5e7eb",
      borderRadius: "15px",
      padding: "20px",
      width: "280px",
      height: "340px",
      fontFamily: "Arial, sans-serif",
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-start",
      justifyContent: "flex-start",
    },
    fallbackFormPreviewSpecial: {
      backgroundColor: "#f9fafb",
      border: "2px solid #e5e7eb",
      borderRadius: "15px",
      padding: "20px",
      width: "280px",
      height: "340px",
      fontFamily: "Arial, sans-serif",
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-start",
      justifyContent: "flex-start",
    },
    fallbackFormHeader: {
      fontSize: "16px",
      fontWeight: "bold",
      textAlign: "center",
      marginBottom: "20px",
      paddingBottom: "10px",
      borderBottom: "1px solid #374151",
      width: "100%",
    },
    fallbackFormHeaderSpecial: {
      fontSize: "16px",
      fontWeight: "bold",
      textAlign: "center",
      marginBottom: "20px",
      paddingBottom: "10px",
      borderBottom: "1px solid #374151",
      width: "100%",
    },
    fallbackFormContent: {
      display: "flex",
      flexDirection: "column",
      gap: "10px",
      width: "100%",
    },
    fallbackCheckbox: {
      fontSize: "12px",
      color: "#6b7280",
    },
    fallbackLine: {
      height: "1px",
      backgroundColor: "#d1d5db",
      width: "100%",
      margin: "5px 0",
    },
    cardContent: {
      flex: "1",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: isMobile ? "center" : "flex-start",
      textAlign: isMobile ? "center" : "left",
      height: "100%",
      padding: "20px 0",
    },
    specialCardContent: {
      flex: "1",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: isMobile ? "center" : "flex-start",
      textAlign: isMobile ? "center" : "left",
      height: "100%",
      padding: "20px 0",
    },
    cardTitle: {
      fontSize: "28px",
      fontWeight: "600",
      color: "#000000",
      margin: "0 0 20px 0",
    },
    specialCardTitle: {
      fontSize: "28px",
      fontWeight: "600",
      color: "#000000",
      margin: "0 0 20px 0",
    },
    cardPrice: {
      fontSize: "16px",
      color: "#6b7280",
      fontWeight: "500",
      margin: "0 0 25px 0",
    },
    specialCardPrice: {
      fontSize: "16px",
      color: "#6b7280",
      fontWeight: "500",
      margin: "0 0 25px 0",
    },
    fillBtn: {
      backgroundColor: "white",
      color: "#000000",
      border: "2px solid #000000",
      padding: "15px 30px",
      borderRadius: "10px",
      fontSize: "16px",
      fontWeight: "600",
      cursor: "pointer",
      transition: "all 0.3s ease",
    },
    specialFillBtn: {
      backgroundColor: "white",
      color: "#000000",
      border: "2px solid #000000",
      padding: "15px 30px",
      borderRadius: "10px",
      fontSize: "16px",
      fontWeight: "600",
      cursor: "pointer",
      transition: "all 0.3s ease",
    },
    navButtonPrev: {
      position: "absolute",
      left: isMobile ? "10px" : "20px",
      top: "50%",
      transform: "translateY(-50%)",
      backgroundColor: "#7fb3a3",
      color: "white",
      border: "none",
      borderRadius: "50%",
      width: isMobile ? "45px" : "60px",
      height: isMobile ? "45px" : "60px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      boxShadow: "0 4px 15px rgba(0,0,0,0.2)",
      transition: "all 0.3s ease",
      zIndex: 10,
    },
    navButtonNext: {
      position: "absolute",
      right: isMobile ? "10px" : "20px",
      top: "50%",
      transform: "translateY(-50%)",
      backgroundColor: "#7fb3a3",
      color: "white",
      border: "none",
      borderRadius: "50%",
      width: isMobile ? "45px" : "60px",
      height: isMobile ? "45px" : "60px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      boxShadow: "0 4px 15px rgba(0,0,0,0.2)",
      transition: "all 0.3s ease",
      zIndex: 10,
    },
  };

  const customSwiperStyles = `
    .swiper-pagination-bullet-custom {
      width: 12px;
      height: 12px;
      background: #d1d5db;
      border-radius: 50%;
      display: inline-block;
      margin: 0 6px;
      cursor: pointer;
      transition: all 0.3s ease;
    }
    .swiper-pagination-bullet-active-custom {
      background: #7fb3a3;
      transform: scale(1.3);
    }
    .swiper-button-prev-custom:hover,
    .swiper-button-next-custom:hover {
      background-color: #6a9b8e !important;
      transform: translateY(-50%) scale(1.1) !important;
    }
    
    @media (max-width: 768px) {
      .swiper-button-prev-custom,
      .swiper-button-next-custom {
        width: 45px !important;
        height: 45px !important;
      }
      .swiper-button-prev-custom {
        left: 10px !important;
      }
      .swiper-button-next-custom {
        right: 10px !important;
      }
    }
  `;

  return (
    <div style={styles.container}>
      <style>{customSwiperStyles}</style>

      <header style={styles.header}>
        <div style={styles.headerLeft}>
         
          <img
            src="/logo.png"
            alt="Shinnyo Logo"
            onClick={() => navigate("/home")}
            style={{
              height: "60px",
              objectFit: "contain",
              display: isMobile && !sidebarCollapsed ? "none" : "block",
              cursor: "pointer",
              transition: "opacity 0.2s ease",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.8")}
            onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
          />
           <button
            style={styles.menuButton}
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          >
            {sidebarCollapsed ? <MenuIcon /> : <CloseIcon />}
          </button>
          {!isMobile && (
            <button
              style={styles.goBackButton}
              onClick={() => window.history.back()}
            >
              <ArrowLeftIcon />
              Go Back
            </button>
          )}
        </div>

        <div style={styles.headerRight}>
          <div style={styles.cartIcon} onClick={() => navigate("/basket")}>
                      <FontAwesomeIcon
                        icon={faShoppingBasket}
                        style={{ marginLeft: "8px" }}
                      />
                      <span style={styles.cartBadge}>{cartCount}</span>
                    </div>
          <button
            style={styles.signOutButton}
            onClick={() => navigate("/login")}
          >
            Sign Out
          </button>
        </div>
      </header>

      {isMobile && (
        <div style={styles.overlay} onClick={() => setSidebarCollapsed(true)} />
      )}

      <nav style={styles.sidebar}>
        {menuItems.map((item, index) => {
          const IconComponent = item.icon;
          return (
            <div
              key={index}
              style={{
                ...styles.menuItem,
                ...(activeMenuItem === item.name ? styles.menuItemActive : {}),
                ...(sidebarCollapsed ? styles.menuItemCollapsed : {}),
              }}
              onClick={() => {
                setActiveMenuItem(item.name);
                if (isMobile) setSidebarCollapsed(true);
                if (item.name === "My Basket") navigate("/basket");
                else if (item.name === "Home") navigate("/home");
                else if (item.name === "Sign Out") navigate("/login");
                else if (item.name === "Online Prayer Requests") navigate("/#");
                else if (item.name === "Upload Prayer Forms") navigate("/upload-forms");
                else if (item.name === "Download Prayer Forms") navigate("/download-forms");
                else if (item.name === "Payments & Offerings") navigate("/Offerings");
              }}
              onMouseEnter={(e) => {
                if (activeMenuItem !== item.name) {
                  e.currentTarget.style.backgroundColor = "#4b5563";
                }
              }}
              onMouseLeave={(e) => {
                if (activeMenuItem !== item.name) {
                  e.currentTarget.style.backgroundColor = "#89b0ae";
                }
              }}
            >
              <div style={styles.menuItemLeft}>
                <IconComponent />
                <span style={styles.menuItemText}>{item.name}</span>
              </div>
              {item.hasSubmenu && !sidebarCollapsed && <ChevronRightIcon />}
            </div>
          );
        })}
      </nav>

      <main style={styles.mainContent}>
        <div style={styles.titleSection}>
          <h1 style={styles.pageTitle}>
            Choose the type of prayer request you would like to submit
          </h1>
        </div>

        <div style={styles.swiperContainer}>
          <Swiper
            modules={[Navigation]}
            spaceBetween={50}
            slidesPerView={1}
            navigation={{
              nextEl: ".swiper-button-next-custom",
              prevEl: ".swiper-button-prev-custom",
            }}
            centeredSlides={true}
            loop={true}
            style={{ padding: "0 120px" }}
          >
            <SwiperSlide>
              <div style={styles.slideContainer}>
                <div style={styles.regularCard}>
                  <div style={styles.formPreviewSection}>
                    <img
                      src="/homa-request-regular-thumb.jpg"
                      alt="Regular Homa Request Form"
                      style={styles.formPreviewImg}
                      onError={(e) => {
                        e.target.style.display = "none";
                        e.target.nextSibling.style.display = "flex";
                      }}
                    />
                    <div style={{ ...styles.fallbackFormPreview, display: "none" }}>
                      <div style={styles.fallbackFormHeader}>REQUEST FOR HOMA</div>
                      <div style={styles.fallbackFormContent}>
                        <div style={styles.fallbackCheckbox}>☐ Buddha</div>
                        <div style={styles.fallbackCheckbox}>☐ Seiryo-Daigongen</div>
                        <div style={styles.fallbackCheckbox}>☐ Bodhisattva Metsuakushu</div>
                        <div style={styles.fallbackLine}></div>
                        <div style={styles.fallbackLine}></div>
                        <div style={styles.fallbackLine}></div>
                      </div>
                    </div>
                  </div>
                  <div style={styles.cardContent}>
                    <h2 style={styles.cardTitle}>Regular Homa Request</h2>
                    <p style={styles.cardPrice}>Suggested offering is £1.50 per request</p>
                    <button style={styles.fillBtn} onClick={() => navigate("/regular")}>
                      Fill Online
                    </button>
                  </div>
                </div>
              </div>
            </SwiperSlide>

            <SwiperSlide>
              <div style={styles.slideContainer}>
                <div style={styles.specialCard}>
                  <div style={styles.formPreviewSection}>
                    <img
                      src="/homa-request-special-thumb.jpg"
                      alt="Special Homa Request Form"
                      style={styles.formPreviewImgSpecial}
                      onError={(e) => {
                        e.target.style.display = "none";
                        e.target.nextSibling.style.display = "flex";
                      }}
                    />
                    <div style={{ ...styles.fallbackFormPreviewSpecial, display: "none" }}>
                      <div style={styles.fallbackFormHeaderSpecial}>REQUEST FOR SPECIAL HOMA</div>
                      <div style={styles.fallbackFormContent}>
                        <div style={styles.fallbackCheckbox}>☐ Buddha</div>
                        <div style={styles.fallbackCheckbox}>☐ Seiryo-Daigongen</div>
                        <div style={styles.fallbackCheckbox}>☐ Bodhisattva Metsuakushu</div>
                        <div style={styles.fallbackLine}></div>
                        <div style={styles.fallbackLine}></div>
                        <div style={styles.fallbackLine}></div>
                      </div>
                    </div>
                  </div>
                  <div style={styles.specialCardContent}>
                    <h2 style={styles.specialCardTitle}>Special Homa Request</h2>
                    <p style={styles.specialCardPrice}>Suggested offering is £30</p>
                    <button style={styles.specialFillBtn} onClick={() => navigate("/special")}>
                      Fill Online
                    </button>
                  </div>
                </div>
              </div>
            </SwiperSlide>
          </Swiper>

          <div className="swiper-button-prev-custom" style={styles.navButtonPrev}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
              <polyline points="15,18 9,12 15,6"></polyline>
            </svg>
          </div>
          <div className="swiper-button-next-custom" style={styles.navButtonNext}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
              <polyline points="9,18 15,12 9,6"></polyline>
            </svg>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Homarequests;